import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router";
import useUserStore from "../../../stores/store";
import ApiService from "../../../utils/ApiService";
import {
  calculateSummary,
  calculateTotal,
  checkAndGetExistingCartItem,
  defaultInputs,
  getFoodUnitPrice,
  getFormatedFoodsBeforeSubmit,
  getInitFormInputs,
  getQueryParamsOrderForm,
  isStockAvailable,
  makeCompatibleAdditionGroups,
  mergeIdenticalFoodItemsGrid,
  validateAndGetTableNumber,
  validateOrder,
} from "../../../services/order-add-edit-service";
import OrderPaymentModal from "../../modals/OrderPaymentModal";
import OrderCategorySlider from "./OrderCategorySlider";
import OrderFoodsGrid from "./OrderFoodsGrid";
import OrdersGridOrdersSummary from "./OrdersGridOrderSummary";
import { getOrCreateSessionId, getParsedNumber } from "../../../utils/Common";
import { processAndSendPrintCommand, shouldCheckedPayLater } from "../../../services/order-processing-service";
import OrderFormInputsComponent from "./OrderFormInputsComponent";
import {
  formatAsOrderListFromFormData,
  navToPayment,
  redirectToTargetPage,
} from "../../../services/order-common-service";
import useTableManagementStorage from "../../../stores/useTableManamentStore";

let uniqueCartItems = [];
const sessionId = getOrCreateSessionId();

export default function OrderFormComponent({
  context,
  availableFoods,
  totalAvailableFoods,
  availableCategories,
  selectedCategory,
  setSelectedCategory,
  selectedFood,
  setSelectedFood,
  orderRecord,
}) {
  const navigate = useNavigate();
  let params = useParams();
  const { isTakeout, isTableManagement, sourcePage, orderPageNo } = getQueryParamsOrderForm();
  const {
    user,
    isPayLater,
    restaurantId,
    isVatIncluded,
    restaurantServiceCharge,
    restaurantVat,
    loyaltyProgramSettings,
    showNotification,
  } = useUserStore();
  const { idWiseGuestNumber } = useTableManagementStorage();
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [orderDetails, setOrderDetails] = useState({});
  const [orderedFoodItems, setOrderedFoodItems] = useState([]);
  const [upsertLoading, setUpsertLoading] = useState(false);
  const [subTotal, setSubTotal] = useState(0);
  const [totalSC, setTotalSC] = useState(0);
  const [totalSd, setTotalSd] = useState(0);
  const [totalVat, setTotalVat] = useState(0);
  const [grossTotal, setGrossTotal] = useState(0);
  const [payAfterEat, setPayAfterEat] = useState(orderRecord.payAfterEat || false);
  const [selectedFoods, setSelectedFoods] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [inputs, setInputs] = useState({ ...defaultInputs });
  const orderSubmitRef = useRef(false);
  const lastClickRef = useRef(0);

  function onChageInputs(name, value) {
    setInputs((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "orderType" && value === "Takeout") {
      setPayAfterEat(false);
    }
  }

  function onHandleRemoveFood(fuid) {
    setSelectedFoods((prevFoods) => prevFoods.filter((food) => food.fuid !== fuid));
    setOrderedFoodItems((prevItems) => prevItems.filter((item) => item.fuid !== fuid));
  }

  /**
   * Case1: Option change
   * Case2: Quantity change
   * Case2.1: Handle special case for quantity. The food which will have add-ons or meal additions will create another set of item instead of increasing quantity.
   * Increament or decreament for other foods will behave as usual. Note that fuid is only available inside ordered food hence it is handle here
   * @param {*} fuid
   * @param {*} key
   * @param {*} value it can be optionId or quantity
   */
  function updateFoodItem(fuid, key, value) {
    let differentSet = null;
    setOrderedFoodItems((prev) => {
      // on option id, change the unitPrice of ordered food item
      const cartItem = prev.find((fi) => fi.fuid === fuid);
      let foodUnitPrice = cartItem.unitPrice;

      if (key === "optionId") {
        foodUnitPrice = getFoodUnitPrice(cartItem.foodId, value, totalAvailableFoods);
      }

      // Handle special case for quantity.
      if (key === "quantity" && cartItem.hasAddOnsOrMealAdditions) {
        differentSet = cartItem;
        return prev.map((item) => item);
      }

      return prev.map((item) => (item.fuid === fuid ? { ...item, [key]: value, unitPrice: foodUnitPrice } : item));
    });

    // It is handled here to avoid state update at the same time from this method and onHandleAddFoodInSelectedFoodList
    if (!differentSet) {
      return;
    }

    const uid = crypto.randomUUID();
    const orgFood = availableFoods.find((item) => item.id === differentSet.foodId);
    const orgFoodWithFuid = { ...orgFood, fuid: uid };

    setSelectedFood(orgFoodWithFuid);
    setSelectedFoods((prev) => [...prev, orgFoodWithFuid]);
    setOrderedFoodItems((prev) => [...prev, { ...differentSet, fuid: uid }]);
  }

  function onHandleAddFoodInSelectedFoodList(food, optionId) {
    const compatibleGroups = makeCompatibleAdditionGroups(food.additionGroups);
    const existingItem = checkAndGetExistingCartItem(orderedFoodItems, food.id, optionId, [], compatibleGroups);

    let tmpQty = existingItem ? existingItem.quantity : 0;

    if (!isStockAvailable(food.id, optionId, ++tmpQty, availableFoods)) {
      showNotification("Sorry stock is not available for now!");
      return;
    }

    const selectedOption = food.options.find((op) => op.optionId === optionId);
    const hasAddOnsOrMealAdditions = food.addOns.length > 0 || food.additionGroups.length > 0 ? true : false;
    const fuid = crypto.randomUUID();
    const foodWithUid = { fuid: fuid, ...food };
    const orderedFoodSnapShot = {
      fuid: fuid,
      foodType: food.foodType,
      foodId: food.id,
      name: food.name,
      optionId: optionId,
      optionName: selectedOption.name,
      unitPrice: selectedOption.currentPrice,
      quantity: 1,
      addOns: [],
      additionGroups: compatibleGroups,
      hasAddOnsOrMealAdditions,
      isVatApplicable: food.isVatApplicable,
      isSupplementaryDutyApplicable: food.isSupplementaryDutyApplicable,
      supplementaryDuty: food.supplementaryDuty
    };

    let existingCartItem = null;

    if (!hasAddOnsOrMealAdditions) {
      existingCartItem = checkAndGetExistingCartItem(orderedFoodItems, food.id, optionId, [], compatibleGroups);
    }

    if (existingCartItem) {
      updateFoodItem(existingCartItem.fuid, "quantity", ++existingCartItem.quantity);
      return;
    }

    if (food !== "") {
      setSelectedFood(foodWithUid);
      setSelectedFoods((prev) => [...prev, foodWithUid]);
      setOrderedFoodItems((prev) => [...prev, orderedFoodSnapShot]);
    }
  }

  function onHandlePayAfterEat(e) {
    const isChecked = e.target.checked;

    if (isChecked === true) {
      setPayAfterEat(true);
      onChageInputs("orderType", "Dine-in");
    }
    if (isChecked === false) {
      setPayAfterEat(false);
    }
  }

  async function upsertOrder(param) {
    setUpsertLoading(true);

    if (param.tableNumber) {
      param.tableNumber = Number(param.tableNumber);
    }

    // TODO: API response changed need to adjust it.
    // to adjust it we will need to get the order submit data in order payment modal and

    const response = await ApiService.UPSERT_ORDER(param, user.token.token);

    if (!response || (response && response.code !== 200)) {
      showNotification(response.data.message);
      setUpsertLoading(false);
      return;
    }

    // Sending auto print request when payAfterEat is YES
    if (param.payAfterEat === "YES") {
      const { record } = response.payload;
      processAndSendPrintCommand(record, user.token.token, showNotification, 1, false);
    }

    if (param.payAfterEat === "YES" || param.orderId) {
      const msg = param.orderId ? "Order updated successfully." : "Order created successfully.";
      showNotification(msg, false);
      redirectToTargetPage(navigate);
      return;
    }

    const allParams = {
      ...param,
      isVatIncluded,
      serviceChargePercent: restaurantServiceCharge,
      vatPercent: restaurantVat,
    };

    const formatedOrderDetails = formatAsOrderListFromFormData(response.payload.record, allParams);

    if (isTableManagement) {
      const takeout = inputs.orderType === "Dine-in" ? "No" : "Yes";
      navToPayment(navigate, formatedOrderDetails.orderId, sourcePage, takeout, orderPageNo);
      return;
    }

    setOrderDetails(formatedOrderDetails);
    setIsPaymentModalOpen(true);
  }

  function onHandleOrderUpsert() {
    const now = Date.now();

    if (now - lastClickRef.current < 800) {
      console.warn("Double click detected, ignoring...");
      return;
    }

    lastClickRef.current = now;
    onHandleOrderUpsertSubmit();
  }

  async function onHandleOrderUpsertSubmit() {
    if (orderSubmitRef.current) {
      return;
    }

    orderSubmitRef.current = true;

    const payAfterEatValue = payAfterEat ? "YES" : "NO";
    const cookingTime = getParsedNumber(inputs.preparingTime);

    uniqueCartItems = mergeIdenticalFoodItemsGrid(orderedFoodItems);

    const reformatedFoods = getFormatedFoodsBeforeSubmit(uniqueCartItems);

    const actualPhone = inputs.mobileNo || user.phone;
    const errorMsg = validateOrder(context, inputs.countryCode, actualPhone, reformatedFoods, cookingTime);
    const tableInfo = validateAndGetTableNumber();

    if (errorMsg || tableInfo.errorMsg) {
      showNotification(errorMsg || tableInfo.errorMsg);
      orderSubmitRef.current = false;
      return;
    }

    let orderObj = {
      countryCode: inputs.countryCode,
      phone: actualPhone,
      restaurantId: restaurantId,
      totalPrice: grossTotal,
      tables: tableInfo.tables,
      guests: idWiseGuestNumber[sessionId],
      tableNumber: inputs.tableNo,
      payAfterEat: payAfterEatValue,
      orderType: inputs.orderType,
      takeoutType: inputs.takeoutType,
      foods: reformatedFoods,
      cookingTime: inputs.preparingTime,
      comment: inputs.specialInstruction,
    };

    if (params.orderId) {
      orderObj = {
        ...orderObj,
        orderId: params.orderId,
      };
    }

    await upsertOrder(orderObj);
    orderSubmitRef.current = false;
  }

  const onFinishPaymentModal = async (result) => {
    if (result.success) {
      setIsPaymentModalOpen(false);

      showNotification("Order created successfully.", false);
      redirectToTargetPage(navigate);
    }
  };

  const onClosePaymentModal = () => {
    setIsPaymentModalOpen(false);
    redirectToTargetPage(navigate);
  };

  function onHandleCancel() {
    redirectToTargetPage(navigate);
  }

  // Filter for search
  const filteredFoods = (availableFoods || []).filter((food) =>
    // food.name.toLowerCase().includes(searchText.toLowerCase())
    food.name.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  /** orderRecord modify foods section for edit only **/
  useEffect(() => {
    function initWhileEdit(orderRecordFoods) {
      const existingFoods = [
        ...orderRecordFoods.map((existFood) => {
          return {
            fuid: existFood.fuid,
            ...totalAvailableFoods.find((avFood) => avFood.id === existFood.foodId),
          };
        }),
      ];

      // push it in selected foods
      setSelectedFoods((prevFoods) => [...existingFoods]);
      setOrderedFoodItems(orderRecordFoods);
    }

    const recordedFood = [...orderRecord.foods];
    if (context === 1 && recordedFood.length > 0 && Boolean(totalAvailableFoods) && totalAvailableFoods.length > 0) {
      initWhileEdit(recordedFood);
    }
  }, [orderRecord, totalAvailableFoods, context]);

  /** foods total price calculations */
  useEffect(() => {
    if (orderedFoodItems.length > 0) {
      const { total, calculatedSd, calculatedSc, calculatedVat, actualTotal } = calculateSummary(orderedFoodItems);
      
      setSubTotal(total);
      setTotalSd(calculatedSd);
      setTotalSC(calculatedSc);
      setTotalVat(calculatedVat);
      setGrossTotal(actualTotal);
    } else {
      setSubTotal(0);
      setTotalSd(0);
      setTotalSC(0);
      setTotalVat(0);
      setGrossTotal(0);
    }
  }, [orderedFoodItems]);

  useEffect(() => {
    const shouldHavePayLater = shouldCheckedPayLater(orderRecord, isPayLater);
    setPayAfterEat(shouldHavePayLater);
  }, [orderRecord, isPayLater]);

  useEffect(() => {
    const tmpInputs = getInitFormInputs(context, orderRecord, isTakeout, user, loyaltyProgramSettings);
    setInputs(tmpInputs);

    if (isTakeout) {
      setPayAfterEat(false);
    }
  }, [orderRecord, user, loyaltyProgramSettings]);

  return (
    <section>
      <div className="w-full h-full flex items-start gap-x-[20px]">
        <div className="min-w-[47%] w-[47%] h-full">
          <div className="w-full">
            <OrderCategorySlider
              categories={availableCategories}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
            />
          </div>

          {/*Search Bar*/}
          <div className="w-full flex gap-2 mb-4 mt-[30px]">
            <input
              type="text"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              placeholder="Search Foods"
              className="w-full px-3 py-1 border rounded-[4px] outline-none"
            />
          </div>

          <div className="w-full min-h-[480px] border border-[1px] border-[#E4E4E4] bg-white">
            <OrderFoodsGrid
              foodList={filteredFoods}
              selectedFood={selectedFood}
              addFoodInSelectedFoods={onHandleAddFoodInSelectedFoodList}
            />
          </div>
        </div>
        <div className="w-full lg:min-w-[50%] lg:w-[50%] h-full lg:mr-20">
          {/* meta info */}
          <OrderFormInputsComponent
            context={context}
            orderRecord={orderRecord}
            fields={inputs}
            onChangeFields={onChageInputs}
          />

          {/* order summary */}
          <div className="mt-4">
            <OrdersGridOrdersSummary
              context={context}
              isPaidOrder={orderRecord.paymentStatus === 1}
              selectedFoods={selectedFoods}
              handleUpsertOrder={onHandleOrderUpsert}
              handleCancel={onHandleCancel}
              orderedFoodItems={orderedFoodItems}
              updateFoodItem={updateFoodItem}
              payAfterEat={payAfterEat}
              handlePayAfterEat={onHandlePayAfterEat}
              upsertLoading={upsertLoading}
              handleRemoveFoodItem={onHandleRemoveFood}
              priceSummary={{
                subTotal: subTotal,
                totalSd: totalSd,
                totalSC: totalSC,
                totalVat: totalVat,
                grossTotal: grossTotal,
              }}
              orderType={inputs.orderType}
              availableFoods={totalAvailableFoods}
            />
          </div>
        </div>
      </div>
      <OrderPaymentModal
        totalAmount={grossTotal}
        shouldShowCookingTime={false}
        cookingTime={inputs.preparingTime}
        openModal={isPaymentModalOpen}
        params={{
          onFinish: onFinishPaymentModal,
          onCancel: onClosePaymentModal,
          order: orderDetails,
        }}
      />
    </section>
  );
}
